import { CSSProperties } from "react";
import { Cell, Column, flexRender, Header, Table } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { TableCell } from "@/components/ui/table";

interface TCellProps<T extends Record<string, any>> {
  table: Table<T>;
  cell: Cell<T, unknown>;
  header: Header<T, unknown>;
}

const TCell = <T extends Record<string, any>>({
  table,
  cell,
  header,
}: TCellProps<T>) => {
  const getCommonPinningStyles = (column: Column<T>): CSSProperties => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
      isPinned === "right" && column.getIsFirstColumn("right");

    return {
      boxShadow: isLastLeftPinnedColumn
        ? "-4px 0 4px -4px lightgray inset"
        : isFirstRightPinnedColumn
        ? "4px 0 4px -4px lightgray inset"
        : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
      position: isPinned ? "sticky" : "relative",
      zIndex: isPinned ? 1 : 0,
    };
  };
  return (
    <TableCell
      style={{
        width: cell.column.getSize(),
        ...getCommonPinningStyles(cell.column),
      }}
      className={cn("text-left group/cell", {
        "flex-1": table.getAllColumns().length - 1 === cell.column.getIndex(),
      })}
    >
      <div className="min-h-12 text-sm flex items-center break-all px-1 w-full">
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
      {cell.column.getCanResize() &&
        cell.column.getIndex() !== table.getAllColumns().length - 1 && (
          <div
            aria-hidden="true"
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            className={cn(
              "absolute right-0 top-0 flex justify-end h-full w-2 cursor-col-resize select-none touch-none "
            )}
          >
            <div
              className={cn(
                "w-[1px] h-full bg-transparent peer-hover:bg-gray-800 group-hover/cell:bg-gray-800 transition-all",
                {
                  "bg-gray-800 hover:bg-gray-800": cell.column.getIsResizing(),
                }
              )}
            />
          </div>
        )}
    </TableCell>
  );
};

export default TCell;
